import React from "react";
import { Field, Form, Formik } from "formik";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Button,
  Input,
  Select,
  border,
  useBoolean,
} from "@chakra-ui/react";
import styles from "./style.module.scss";
import FormSelect from "../FormSelect";
import { contactUs, ContactUsType } from "../../api/businessApi";
import { Dictionary } from "@/types/base";
import { useOwnToast } from "@/utils/misc";

const ScaleMap = [
  {
    key: "1",
    value: "10人以下",
  },
  {
    key: "2",
    value: "10~50人",
  },
  {
    key: "3",
    value: "50~100人",
  },
  {
    key: "4",
    value: "100~500人",
  },
  {
    key: "5",
    value: "500人以上",
  },
];

const InputStyle = {
  height: "40px",
  fontSize: "16px",
  width: "340px",
  backgroundColor: "white",
  color: "#86909C",
  border: "1px solid #E5E6EB",
  borderColor: "#E5E6EB",
  _focus: {
    borderColor: "#86909C",
  },
  _placeholder: {
    color: "#C9CDD4",
  },
  _focusVisible: {
    borderColor: "#E5E6EB",
  },
  // autoComplete: "off",
};

const LabelStyle = {
  color: "#86909C",
  fontSize: "14px",
  margin: 0,
};

const ErrMessageStyle = {
  tt: "tt",
};
interface ContactFormProps {
  afterSubmit?(): void;
}

const SUBMITTED_STORAGE_KEY = "business_submitted";
const ContactForm: React.FC<ContactFormProps> = ({ afterSubmit }) => {
  const toast = useOwnToast();
  const [isSubmitted, submittedOp] = useBoolean(!!localStorage.getItem(SUBMITTED_STORAGE_KEY));
  const validateName = (value: any) => {
    let error;
    if (String(value).length > 50) {
      error = `姓名最多可以输入50个字符, 当前有${String(value).length}个`;
    } else if (!value) {
      error = "请输入您的姓名";
    }
    return error;
  };

  const validatePhoneNum = (value: any) => {
    let error;
    if (!/^1[3456789]\d{9}$/.test(value)) {
      error = "请输入正确格式的手机号";
    } else if (!value) {
      error = "请输入您的手机号";
    }
    return error;
  };

  const validateStringNum = (value: any) => {
    let error;
    if (String(value).length > 100) {
      error = `最多可以输入50个字符, 当前有${String(value).length}个`;
      return error;
    }
  };

  const validateEnterpriseName = (value: any) => {
    let error;
    if (!value) {
      error = "请输入您的企业名称";
      return error;
    }
    return validateStringNum(value);
  };

  const formatFormValue = (obj: Dictionary<any>) => {
    const res: any = {};
    const keys = Object.keys(obj);
    keys.forEach((key) => {
      if (key === "staff_size") {
        res[key] = Number(obj[key]) || 0;
      } else {
        res[key] = String(obj[key]);
      }
    });
    return res;
  };

  return (
    <Formik
      initialValues={{
        name: "",
        phone: "",
        enterprise_name: "",
        dept_name: "",
        industry: "",
        staff_size: "",
      }}
      onSubmit={async (values, actions) => {
        // setTimeout(() => {
        //   alert(JSON.stringify(values, null, 2));
        //   actions.setSubmitting(false);
        // }, 1000);

        const formatForm = formatFormValue(values);
        const res = await contactUs(formatForm);
        if (res.code === 0) {
          submittedOp.on();
          localStorage.setItem(SUBMITTED_STORAGE_KEY, "true");
          toast({
            title: "信息已发送",
            description: "我们会尽快给您回复！",
            status: "success",
            duration: 3000,
            position: "top",
          });
          afterSubmit?.();
        }
        actions.setSubmitting(false);
      }}
    >
      {(props) => (
        <Form className={styles["contact-form-wrapper"]}>
          {/* 手机号 */}
          <Field name="phone" validate={validatePhoneNum}>
            {({ field, form }: any) => (
              <FormControl
                isInvalid={form.errors.phone && form.touched.name}
                className={styles["form-item-wrapper"]}
              >
                <FormLabel {...LabelStyle}>手机号 (必填):</FormLabel>
                <Input {...field} placeholder="请输入11位手机号" type={"number"} {...InputStyle} />
                <FormErrorMessage className={styles["form-err-msg"]}>
                  {form.errors.phone}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>

          {/* 姓名 */}
          <Field name="name" validate={validateName}>
            {({ field, form }: any) => (
              <FormControl
                isInvalid={form.errors.name && form.touched.name}
                className={styles["form-item-wrapper"]}
              >
                <FormLabel {...LabelStyle}>姓名 (必填):</FormLabel>
                <Input {...field} placeholder="请输入您的姓名" {...InputStyle} />
                <FormErrorMessage className={styles["form-err-msg"]}>
                  {form.errors.name}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>

          {/* 公司 */}
          <Field name="enterprise_name" validate={validateEnterpriseName}>
            {({ field, form }: any) => (
              <FormControl
                isInvalid={form.errors.enterprise_name && form.touched.enterprise_name}
                className={styles["form-item-wrapper"]}
              >
                <FormLabel {...LabelStyle}>公司/团队/机构 (必填):</FormLabel>
                <Input {...field} placeholder="请输入您的公司名称" {...InputStyle} />
                <FormErrorMessage className={styles["form-err-msg"]}>
                  {form.errors.enterprise_name}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>

          {/* 职务 */}
          <Field name="dept_name" validate={validateStringNum}>
            {({ field, form }: any) => (
              <FormControl
                isInvalid={form.errors.dept_name && form.touched.dept_name}
                className={styles["form-item-wrapper"]}
              >
                <FormLabel {...LabelStyle}>职务:</FormLabel>
                <Input {...field} placeholder="请输入您的职务" {...InputStyle} />
                <FormErrorMessage className={styles["form-err-msg"]}>
                  {form.errors.dept_name}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>

          {/* 行业 */}
          <Field name="industry" validate={validateStringNum}>
            {({ field, form }: any) => (
              <FormControl
                isInvalid={form.errors.industry && form.touched.industry}
                className={styles["form-item-wrapper"]}
              >
                <FormLabel {...LabelStyle}>所在行业:</FormLabel>
                <Input {...field} placeholder="请输入您的行业" {...InputStyle} />
                <FormErrorMessage className={styles["form-err-msg"]}>
                  {form.errors.industry}
                </FormErrorMessage>
              </FormControl>
            )}
          </Field>

          {/* 人员规模 */}
          <Field name="staff_size">
            {({ field, form }: any) => {
              return (
                <FormControl
                  // isInvalid={form.errors.name && form.touched.name}
                  className={styles["form-item-wrapper"]}
                >
                  <FormLabel {...LabelStyle}>人员规模:</FormLabel>
                  <FormSelect
                    selectedVal={field.value}
                    onChange={(value: string) => props.setFieldValue("staff_size", value)}
                    optionList={ScaleMap}
                  />
                </FormControl>
              );
            }}
          </Field>
          <div className={styles["btn-wrapper"]}>
            <Button
              bg="#165DFF"
              w="140px"
              _hover={{ bg: "#165DFF", opacity: 0.75 }}
              _active={{ bg: "#165DFF", opacity: 0.75 }}
              _disabled={{ bg: "165DFF", opacity: 0.5, cursor: "not-allowed" }}
              borderColor="#165DFF"
              isLoading={props.isSubmitting}
              loadingText="提交信息"
              type="submit"
              isDisabled={isSubmitted}
              className={styles["submit-btn"]}
            >
              {isSubmitted ? "已提交" : "提交信息"}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ContactForm;
